import { Button, Card, Flex, Typography } from 'antd'
import { Copy, ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { CodeSnippet, useSnippetTipsStyles } from 'src/components'

import { APP_ENDPOINT_PLACEHOLDER } from './constants'

const { Text } = Typography

/** Placeholder shown when the real token isn't available on this page load. */
const TOKEN_PLACEHOLDER = '<YOUR_API_TOKEN>'

type Props = {
  /**
   * Real token if the user just created/rotated this credential; otherwise
   * we render a placeholder so the example is still useful.
   */
  token: string | null
  /**
   * Optional endpoint substituted into the curl sample. Pass on app-scoped
   * pages where a single bound app is known (its base URL / domain); the
   * public-access page, which spans multiple apps, leaves this unset and
   * gets the `<APP_ENDPOINT>` placeholder.
   */
  endpoint?: string | null
  /**
   * Link to the bound app's public API docs (Swagger). Rendered as a header
   * action when present; the multi-app public-access page leaves it unset.
   */
  docsUrl?: string | null
}

const buildCurlExample = (token: string, endpoint: string) =>
  [
    // A GET against the app's public docs — a real, side-effect-free call to
    // confirm the token authenticates. Per-operation endpoints/bodies live in
    // the docs themselves.
    `curl -X GET '${endpoint.replace(/\/+$/, '')}/public/docs' \\`,
    `  -H 'Authorization: Bearer ${token}' \\`,
    `  -H 'Accept: application/json'`
  ].join('\n')

/**
 * Universal Quick Start guidance for any credential — the token pattern
 * (Authorization: Bearer …) is the same everywhere. Per-app endpoints /
 * request shapes live in each app's own docs; when the surface knows the
 * bound app it can pass `endpoint` and the sample uses the real base URL.
 */
export const QuickStartCard = ({ token, endpoint, docsUrl }: Props) => {
  const { t } = useTranslation()
  const tipsClassName = useSnippetTipsStyles()

  const displayToken = token ?? TOKEN_PLACEHOLDER
  const displayEndpoint = endpoint ?? APP_ENDPOINT_PLACEHOLDER
  const example = buildCurlExample(displayToken, displayEndpoint)
  const endpointResolved = !!endpoint

  const handleCopy = () => {
    void navigator.clipboard.writeText(example)
  }

  return (
    <Card
      title={t('workspace.credentials.quickStart.title')}
      extra={
        <Flex gap={8}>
          {docsUrl ? (
            <Button
              size="small"
              icon={<ExternalLink size={14} />}
              href={docsUrl}
              target="_blank"
              rel="noreferrer"
            >
              {t('workspace.credentials.quickStart.docs')}
            </Button>
          ) : null}
          <Button
            size="small"
            icon={<Copy size={14} />}
            onClick={handleCopy}
            aria-label={t('workspace.credentials.quickStart.copy')}
          >
            {t('workspace.credentials.quickStart.copy')}
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap={12}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {t('workspace.credentials.quickStart.intro')}
        </Text>

        <CodeSnippet>{example}</CodeSnippet>

        {!endpointResolved ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('workspace.credentials.quickStart.endpointHint')}
          </Text>
        ) : null}

        {!token ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('workspace.credentials.quickStart.tokenHint')}
          </Text>
        ) : null}

        <ul className={tipsClassName}>
          <li>{t('workspace.credentials.quickStart.tips.storage')}</li>
          <li>{t('workspace.credentials.quickStart.tips.transport')}</li>
          <li>{t('workspace.credentials.quickStart.tips.rotate')}</li>
          <li>{t('workspace.credentials.quickStart.tips.scope')}</li>
        </ul>
      </Flex>
    </Card>
  )
}
